import React from 'react'
import { useTranslation } from 'react-i18next'
import { CategoryBadge } from '../admin/ScoreComparisonCard'
import {
  interpretMSS,
  interpretDCIS,
  identifyDyadicCopingRisks,
  calculateDCISPotential,
  identifyPersonalityRisks,
} from '../../lib/interpretationEngine'
import {
  TARGETS,
  DCI_SUBSCALE_LEGEND,
} from '../../lib/interpretationConfig'
import CoupleProfileSummary from '../CoupleProfileSummary'

// --- Priority Badge ---

const PRIORITY_STYLES = {
  1: 'bg-red-100 text-red-700',
  2: 'bg-orange-100 text-orange-700',
  3: 'bg-yellow-100 text-yellow-700',
}

function PriorityBadge({ priority }) {
  const colors = PRIORITY_STYLES[priority] || 'bg-gray-100 text-gray-600'
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${colors}`}>
      P{priority}
    </span>
  )
}

// --- Score Interpretation Card ---

const BORDER_COLORS = {
  'Extremely Low': 'border-l-red-500',
  'Low': 'border-l-orange-400',
  'Moderate': 'border-l-yellow-400',
  'High': 'border-l-blue-400',
  'Extremely High': 'border-l-green-400',
  'Below Average': 'border-l-red-500',
  'Normal': 'border-l-gray-400',
  'Above Average': 'border-l-green-400',
}

function ScoreInterpretationCard({ title, interpretation, footnote }) {
  const { score, category, narrative, gap, gapText } = interpretation
  const borderColor = BORDER_COLORS[category] || 'border-l-gray-300'

  return (
    <div className={`bg-white rounded-lg border border-gray-100 border-l-4 ${borderColor} p-5`}>
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-base font-bold text-gray-800">{title}</h3>
        <span className="text-2xl font-bold text-gray-800">{score}</span>
        <CategoryBadge category={category} />
      </div>
      <p className="text-sm text-gray-700 leading-relaxed mb-3">{narrative}</p>
      {gap !== 0 && (
        <div className={`rounded-md p-3 text-sm ${gap > 0 ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'}`}>
          {gapText}
        </div>
      )}
      {gap === 0 && (
        <div className="rounded-md p-3 text-sm bg-green-50 text-green-800">
          {gapText}
        </div>
      )}
      {footnote && (
        <p className="text-xs text-gray-400 mt-2">{footnote}</p>
      )}
    </div>
  )
}

// --- Dyadic Coping Risk Factors Table ---

function DyadicCopingRisksTable({ risks, dciTotal }) {
  const { t } = useTranslation(['report', 'scoring'])

  if (risks.length === 0) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
        <p className="text-sm text-green-700">
          {t('report:interpretationReport.dyadicCopingRisks.noRisks')}
        </p>
      </div>
    )
  }

  const potential = calculateDCISPotential(risks, dciTotal)

  return (
    <div>
      <p className="text-sm text-gray-600 mb-3">
        {t('report:interpretationReport.dyadicCopingRisks.description', { mssTarget: TARGETS.MSS, dcisTarget: TARGETS.DCIS })}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase w-12">{t('report:interpretationReport.dyadicCopingRisks.columnPriority')}</th>
              <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase w-12">{t('report:interpretationReport.dyadicCopingRisks.columnItem')}</th>
              <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase">{t('report:interpretationReport.dyadicCopingRisks.columnRiskFactor')}</th>
              <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase w-16">{t('report:interpretationReport.dyadicCopingRisks.columnSubscale')}</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 uppercase w-14">{t('report:interpretationReport.dyadicCopingRisks.columnDcis')}</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 uppercase w-14">{t('report:interpretationReport.dyadicCopingRisks.columnTarget')}</th>
            </tr>
          </thead>
          <tbody>
            {risks.map((risk) => (
              <tr key={risk.questionId} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="py-2 px-2"><PriorityBadge priority={risk.priority} /></td>
                <td className="py-2 px-2 text-gray-600">{risk.itemNo}.</td>
                <td className="py-2 px-2 text-gray-700">{risk.questionText}</td>
                <td className="py-2 px-2 text-gray-500 font-medium">{risk.subscale}</td>
                <td className="py-2 px-2 text-center font-semibold text-gray-800">{risk.currentScore}</td>
                <td className="py-2 px-2 text-center font-semibold text-green-700">{risk.targetScore}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200">
              <td colSpan={4} className="py-2 px-2 text-sm font-semibold text-gray-700 text-right">
                {t('report:interpretationReport.dyadicCopingRisks.targetedIncrease')}
              </td>
              <td colSpan={2} className="py-2 px-2 text-center font-bold text-gray-800">
                {potential.totalIncrease}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-xs text-gray-400 mt-2">
        {t('report:interpretationReport.dyadicCopingRisks.subscaleLegend')} {Object.entries(DCI_SUBSCALE_LEGEND).map(([k, v]) => `${k} = ${v}`).join('; ')}.
      </p>
      <div className={`rounded-md p-3 text-sm mt-3 ${potential.wouldReachTarget ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
        {t('report:interpretationReport.dyadicCopingRisks.projectedDcis', { current: dciTotal, projected: potential.projectedDCIS })}
        {' '}
        {potential.wouldReachTarget
          ? t('report:interpretationReport.dyadicCopingRisks.wouldReachTarget', { target: TARGETS.DCIS })
          : t('report:interpretationReport.dyadicCopingRisks.wouldNotReachTarget', { target: TARGETS.DCIS })
        }
      </div>
    </div>
  )
}

// --- Personality Risk Factors Table ---

function PersonalityRisksTable({ risks, person1Name, person2Name }) {
  const { t } = useTranslation('report')

  if (risks.length === 0) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
        <p className="text-sm text-green-700">
          {t('interpretationReport.personalityRisks.noRisks')}
        </p>
      </div>
    )
  }

  return (
    <div>
      <p className="text-sm text-gray-600 mb-3">
        {t('interpretationReport.personalityRisks.description')}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase w-12">{t('interpretationReport.personalityRisks.columnPriority')}</th>
              <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase w-16">{t('interpretationReport.personalityRisks.columnFacet')}</th>
              <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase">{t('interpretationReport.personalityRisks.columnRiskFactor')}</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 uppercase w-20">{person1Name}</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 uppercase w-20">{person2Name}</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 uppercase w-14">{t('interpretationReport.personalityRisks.columnDiff')}</th>
            </tr>
          </thead>
          <tbody>
            {risks.map((risk, idx) => (
              <tr key={`${risk.facetRef}-${idx}`} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="py-2 px-2">
                  <PriorityBadge priority={risk.priority} />
                </td>
                <td className="py-2 px-2 font-medium text-gray-600">{risk.facetRef}</td>
                <td className="py-2 px-2 text-gray-700">
                  {risk.facetName}
                  <span className="text-xs text-gray-400 ml-1">({t(`interpretationReport.personalityRisks.priorityLabels.${risk.priority}`)})</span>
                </td>
                <td className="py-2 px-2 text-center font-semibold text-gray-800">{risk.hisScore}</td>
                <td className="py-2 px-2 text-center font-semibold text-gray-800">{risk.herScore}</td>
                <td className="py-2 px-2 text-center font-semibold text-gray-800">{risk.diff ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 mt-2">
        {t('interpretationReport.personalityRisks.footnote')}
      </p>
    </div>
  )
}

// --- Person Report ---

function PersonReport({ person, msInterpretation, dciInterpretation, dyadicRisks, personalityRisks, person1Name, person2Name }) {
  const { t } = useTranslation('report')

  return (
    <div className="space-y-5">
      {/* MSS Interpretation */}
      <ScoreInterpretationCard
        title={t('interpretationReport.mssTitle')}
        interpretation={msInterpretation}
        footnote={t('scoring:mssFootnote')}
      />

      {/* DCIS Interpretation */}
      <ScoreInterpretationCard
        title={t('interpretationReport.dcisTitle')}
        interpretation={dciInterpretation}
        footnote={t('scoring:dcisFootnote')}
      />

      {/* Dyadic Coping Risk Factors */}
      <div className="bg-white rounded-lg border border-gray-100 p-5">
        <h3 className="text-base font-bold text-gray-800 mb-3">{t('interpretationReport.dyadicCopingRisks.title')}</h3>
        <DyadicCopingRisksTable risks={dyadicRisks} dciTotal={person.dci.total} />
      </div>

      {/* Personality Risk Factors */}
      <div className="bg-white rounded-lg border border-gray-100 p-5">
        <h3 className="text-base font-bold text-gray-800 mb-3">{t('interpretationReport.personalityRisks.title')}</h3>
        <PersonalityRisksTable
          risks={personalityRisks}
          person1Name={person1Name}
          person2Name={person2Name}
        />
      </div>
    </div>
  )
}

// --- Main Report ---

export default function InterpretationReport({ person1, person2, msScores }) {
  const { t } = useTranslation(['report', 'scoring'])
  const p1Name = person1.respondent.name
  const p2Name = person2.respondent.name

  // Compute interpretations
  const p1MSSInterp = interpretMSS(msScores.person1.msScore, msScores.person1.category)
  const p2MSSInterp = interpretMSS(msScores.person2.msScore, msScores.person2.category)
  const p1DCISInterp = interpretDCIS(person1.dci.total, person1.dci.category)
  const p2DCISInterp = interpretDCIS(person2.dci.total, person2.dci.category)

  // Identify risk factors
  const p1DyadicRisks = identifyDyadicCopingRisks(person1.dci.itemScores)
  const p2DyadicRisks = identifyDyadicCopingRisks(person2.dci.itemScores)
  const { person1Risks: p1PersonalityRisks, person2Risks: p2PersonalityRisks } = identifyPersonalityRisks(person1.ipip, person2.ipip)

  return (
    <div className="space-y-8">
      <CoupleProfileSummary person1={person1} person2={person2} />

      {/* Preamble */}
      <div className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-6 shadow-sm border border-primary-100">
        <h2 className="text-lg font-bold text-gray-800 mb-2">{t('report:interpretationReport.title')}</h2>
        <p className="text-sm text-gray-600 leading-relaxed">
          {t('report:interpretationReport.preamble', { mssTarget: TARGETS.MSS, dcisTarget: TARGETS.DCIS })}
        </p>
      </div>

      {/* Person 1 Report */}
      <section>
        <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-200">
          {t('report:interpretationReport.reportFor', { name: p1Name })}
        </h2>
        <PersonReport
          person={person1}
          msInterpretation={p1MSSInterp}
          dciInterpretation={p1DCISInterp}
          dyadicRisks={p1DyadicRisks}
          personalityRisks={p1PersonalityRisks}
          person1Name={p1Name}
          person2Name={p2Name}
        />
      </section>

      {/* Person 2 Report */}
      <section>
        <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-200">
          {t('report:interpretationReport.reportFor', { name: p2Name })}
        </h2>
        <PersonReport
          person={person2}
          msInterpretation={p2MSSInterp}
          dciInterpretation={p2DCISInterp}
          dyadicRisks={p2DyadicRisks}
          personalityRisks={p2PersonalityRisks}
          person1Name={p1Name}
          person2Name={p2Name}
        />
      </section>
    </div>
  )
}
